import Head from 'next/head';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { signupUser } from '../services/api';
import { validateToken, verifySession, safeLocalStorage, clearAuthData } from '../utils/auth';

// zxcvbn is large (~800KB). Load it only when someone actually types a password,
// so it never sits in the initial bundle of the funnel's front door.
let _zxcvbn = null;
let _zxcvbnPromise = null;
function loadZxcvbn() {
  if (_zxcvbn) return Promise.resolve(_zxcvbn);
  if (!_zxcvbnPromise) {
    _zxcvbnPromise = import('zxcvbn').then((m) => { _zxcvbn = m.default || m; return _zxcvbn; });
  }
  return _zxcvbnPromise;
}

export default function SignUp() {
  const router = useRouter();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [hasSavedDraft, setHasSavedDraft] = useState(false);
  const [pwStrength, setPwStrength] = useState(null);

  // Clear the error when the user edits something — never on a timer. An error
  // that erases itself after 3s reads as "the button did nothing", which is
  // exactly how this page failed silently.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setError('');
  }, [firstName, lastName, email, password, confirmPassword, agreeToTerms]);

  // Live strength, computed as they type so the bar is never a surprise.
  useEffect(() => {
    let cancelled = false;
    if (!password) { setPwStrength(null); return; }
    loadZxcvbn().then((z) => {
      if (cancelled) return;
      const r = z(password);
      setPwStrength({
        score: r.score,
        warning: (r.feedback && r.feedback.warning) || '',
        suggestions: (r.feedback && r.feedback.suggestions) || [],
      });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [password]);

  // Ask the server, not the token. A signed token for a deleted user used to
  // pass validateToken() and bounce the visitor to /kyc-welcome forever.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await verifySession();
      if (!cancelled && session.authenticated) router.replace('/kyc-welcome');
    })();
    return () => { cancelled = true; };
  }, [router]);

  useEffect(() => {
    try {
      const saved = safeLocalStorage.getItem('signupFormData');
      if (saved) {
        const p = JSON.parse(saved);
        if (p.email && !email) setEmail(p.email);
        if (p.firstName && !firstName) setFirstName(p.firstName);
        if (p.lastName && !lastName) setLastName(p.lastName);
      }
    } catch (e) { console.warn('Failed to load saved form data:', e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try { setHasSavedDraft(!!safeLocalStorage.getItem('signupFormData')); } catch {}
  }, []);

  useEffect(() => {
    if (!(firstName || lastName || email)) return;
    try {
      safeLocalStorage.setItem('signupFormData', JSON.stringify({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        timestamp: Date.now(),
      }));
      setHasSavedDraft(true);
    } catch (e) { console.warn('Failed to save form data:', e); }
  }, [firstName, lastName, email]);

  const validateForm = async () => {
    if (!firstName.trim() || !lastName.trim()) { setError('Full name is required'); return false; }
    if (firstName.trim().length < 2 || lastName.trim().length < 2) { setError('First and last name must be at least 2 characters each'); return false; }
    if (!email.trim()) { setError('Email is required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email address'); return false; }
    if (!password) { setError('Password is required'); return false; }
    if (password.length < 12) { setError('Password must be at least 12 characters long'); return false; }
    const z = await loadZxcvbn();
    const strength = z(password);
    if (strength.score < 3) {
      const suggestions = strength.feedback.suggestions.join(' ') || 'Use a mix of uppercase, lowercase, numbers, and symbols.';
      setError(`Password is too weak. ${suggestions}`);
      return false;
    }
    if (!confirmPassword) { setError('Please confirm your password'); return false; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return false; }
    if (!agreeToTerms) { setError('You must agree to the Terms of Service and Privacy Policy'); return false; }
    return true;
  };

  const clearFormData = () => {
    setFirstName(''); setLastName(''); setEmail(''); setPassword(''); setConfirmPassword('');
    setAgreeToTerms(false); setError('');
    try { safeLocalStorage.removeItem('signupFormData'); setHasSavedDraft(false); }
    catch (e) { console.warn('Failed to clear saved form data:', e); }
  };

  const handleSignUp = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    setError('');
    if (!(await validateForm())) return;
    setIsLoading(true);
    try {
      const result = await signupUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        password: password,
      });
      if (result.success && result.token && result.user) {
        safeLocalStorage.setItem('jwt', result.token);
        try {
          const { normalizeUser } = await import('../utils/auth');
          safeLocalStorage.setItem('user', JSON.stringify(normalizeUser(result.user)));
        } catch {
          safeLocalStorage.setItem('user', JSON.stringify(result.user));
        }
        safeLocalStorage.setItem('registrationComplete', 'true');
        try { safeLocalStorage.removeItem('signupFormData'); setHasSavedDraft(false); } catch {}
        setTimeout(() => router.push('/kyc-welcome'), 300);
      } else {
        throw new Error(result.error || result.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);
      const msg = String(err && (err.message || err));
      if (msg.includes('Authentication') || msg.includes('Token') || err?.status === 401 || err?.status === 403) {
        try { clearAuthData(); } catch {}
      }
      let m = 'Registration could not be completed. Please try again.';
      if (msg) m = msg;
      else if (err?.status === 409) m = 'An account with this email already exists. Please sign in instead.';
      else if (err?.status === 422) m = 'Please check your email format and password requirements.';
      else if (err?.status === 429) m = 'Too many attempts. Please try again later.';
      else if (err?.status >= 500) m = 'Server error. Please try again in a moment.';
      else if (err?.name === 'AbortError' || msg.includes('timeout')) m = 'Request timed out. Please check your connection.';
      else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) m = 'Unable to connect. Please check your internet connection.';
      setError(m);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    try { clearAuthData(); } catch {}
    handleSignUp();
  };

  const ErrorMessage = ({ error, onRetry }) => {
    if (!error) return null;
    const lower = String(error).toLowerCase();
    const retryable = lower.includes('network') || lower.includes('connection') || lower.includes('timeout') || lower.includes('server') || lower.includes('try again');
    return (
      <div className="p-4 mb-6 text-sm text-red-300 bg-red-900/20 border border-red-700 rounded-lg" role="alert" aria-live="polite">
        <p className="font-medium mb-1">Could not create account</p>
        <p>{error}</p>
        {retryable && (
          <div className="mt-3 space-x-2">
            <button type="button" onClick={onRetry} disabled={isLoading}
              className={`text-sm px-3 py-1 rounded border transition-colors ${isLoading ? 'bg-gray-600 border-gray-500 text-gray-400 cursor-not-allowed' : 'bg-red-800/50 hover:bg-red-800/70 border-red-600 text-red-200'}`}>
              {isLoading ? 'Retrying...' : 'Try Again'}
            </button>
            <button type="button" onClick={clearFormData} className="text-sm px-3 py-1 rounded border border-gray-600 text-gray-300 hover:bg-gray-700/50">
              Clear Form
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Head>
        <title>Begin Investor Qualification — KTR Capital</title>
        <meta name="description" content="Begin investor qualification with KTR Capital. The next steps are KYC verification and a brief suitability questionnaire." />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <div className="bg-black text-white min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 max-w-md w-full mx-auto px-6 py-12">

          <div className="text-center mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-4">Step 1 of 4</div>
            <h1 className="text-3xl font-light mb-4">Begin investor qualification</h1>
            <p className="text-gray-400 font-light text-sm leading-relaxed">
              Create an account to begin the KYC and suitability process.
              KTR Capital reviews each prospective investor individually.
            </p>
            {hasSavedDraft && (
              <div className="mt-4 p-3 bg-gray-900/40 border border-gray-700 rounded-lg text-gray-300 text-sm">
                <div className="flex items-center justify-between">
                  <span>Form data restored from previous session.</span>
                  <button onClick={clearFormData} className="text-orange-400 hover:text-orange-300 underline text-xs">Clear</button>
                </div>
              </div>
            )}
          </div>

          <ErrorMessage error={error} onRetry={handleRetry} />

          <form onSubmit={handleSignUp} className="space-y-6" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-light text-gray-300 mb-2">First name</label>
                <input id="firstName" type="text" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-700 bg-gray-900 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-light"
                  disabled={isLoading} required autoComplete="given-name" maxLength={50} minLength={2} />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-light text-gray-300 mb-2">Last name</label>
                <input id="lastName" type="text" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-700 bg-gray-900 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-light"
                  disabled={isLoading} required autoComplete="family-name" maxLength={50} minLength={2} />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-light text-gray-300 mb-2">Email address</label>
              <input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-700 bg-gray-900 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-light"
                autoComplete="email" disabled={isLoading} required maxLength={254} />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-light text-gray-300 mb-2">Password</label>
              <div className="relative">
                <input id="password" type={showPassword ? 'text' : 'password'} placeholder="At least 12 characters" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-700 bg-gray-900 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-light"
                  autoComplete="new-password" disabled={isLoading} required minLength={12} maxLength={128} />
                <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-300"
                  onClick={() => setShowPassword(!showPassword)} tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.962 9.962 0 011.249-2.592M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3l18 18" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 font-light">
                At least 12 characters. Three or four uncommon words work best &mdash; avoid names, dates and common phrases.
              </p>
              {password && (
                <div className="mt-3">
                  <div className="flex gap-1.5 mb-2" aria-hidden="true">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-200 ${
                        pwStrength && pwStrength.score > i
                          ? (pwStrength.score >= 3 ? 'bg-green-500' : pwStrength.score === 2 ? 'bg-yellow-500' : 'bg-red-500')
                          : 'bg-gray-800'
                      }`} />
                    ))}
                  </div>
                  <p role="status" aria-live="polite" className={`text-xs font-light ${
                    !pwStrength ? 'text-gray-500'
                      : pwStrength.score >= 3 ? 'text-green-400'
                      : pwStrength.score === 2 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {!pwStrength
                      ? 'Checking strength\u2026'
                      : pwStrength.score >= 4
                        ? 'Excellent \u2014 well above what we require.'
                        : pwStrength.score === 3
                          ? 'Strong enough. You can continue.'
                          : `${pwStrength.score === 2 ? 'Almost \u2014 not quite strong enough yet.' : 'Too weak to accept.'} ${pwStrength.warning || pwStrength.suggestions[0] || 'Add another uncommon word.'}`}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-light text-gray-300 mb-2">Confirm password</label>
              <input id="confirmPassword" type={showPassword ? 'text' : 'password'} placeholder="Re-enter password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-700 bg-gray-900 text-white rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-light"
                autoComplete="new-password" disabled={isLoading} required minLength={12} maxLength={128} />
            </div>

            <div className="flex items-start space-x-3">
              <input id="terms" type="checkbox" checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="w-4 h-4 mt-1 text-orange-500 border-gray-600 rounded focus:ring-orange-500 bg-gray-900" required disabled={isLoading} />
              <label htmlFor="terms" className="text-sm text-gray-300 cursor-pointer font-light">
                I agree to the <Link href="/terms" className="text-orange-400 hover:text-orange-300 underline" target="_blank">Terms of Service</Link>,{' '}
                <Link href="/privacy" className="text-orange-400 hover:text-orange-300 underline" target="_blank">Privacy Policy</Link>, and{' '}
                <Link href="/disclosures" className="text-orange-400 hover:text-orange-300 underline" target="_blank">Investor Disclosures</Link>.
              </label>
            </div>

            <button type="submit" disabled={isLoading}
              className={`w-full py-4 rounded font-medium text-lg transition-colors ${isLoading ? 'bg-gray-700 cursor-not-allowed opacity-50' : 'bg-orange-500 hover:bg-orange-600 text-black'}`}>
              {isLoading ? 'Creating account...' : 'Continue to KYC'}
            </button>

            {(firstName || lastName || email || password || confirmPassword) && (
              <button type="button" onClick={clearFormData} className="w-full py-2 text-gray-500 hover:text-gray-300 text-sm font-light">Clear form</button>
            )}
          </form>

          <div className="text-center mt-8 text-sm">
            <p className="text-gray-400 font-light">
              Already started qualification?{' '}
              <Link href="/sign-in" className="text-orange-400 hover:text-orange-300 underline">Sign in</Link>
            </p>
          </div>

          <p className="text-xs text-gray-600 mt-10 font-light leading-relaxed text-center">
            KTR Capital is an Exempt Reporting Adviser. Creating an account does not constitute
            an investment, an offer of securities, or a commitment by either party. Eligibility
            to invest is determined after KYC verification and a suitability review.
          </p>
        </main>
        <Footer />
      </div>
    </>
  );
}
